import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import FitScreenIcon from "@mui/icons-material/FitScreen";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { Box, Dialog, IconButton, Tooltip, Typography } from "@mui/material";
import {
  CSSProperties,
  MouseEvent as ReactMouseEvent,
  PointerEvent as ReactPointerEvent,
  WheelEvent as ReactWheelEvent,
  useCallback,
  useRef,
  useState,
} from "react";

import { CurrentChatMessages } from "@/messages/chat/current-chat.messages";

type ChatImagePreviewProps = {
  src: string;
  alt: string;
  fileName: string;
  thumbnailStyle: CSSProperties;
  messages: CurrentChatMessages;
};

type Offset = { x: number; y: number };

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const BUTTON_SCALE_STEP = 0.5;
const WHEEL_SCALE_FACTOR = 0.0015;
const CLICK_ZOOM_FACTOR = 1.5;
const DRAG_CLICK_TOLERANCE_PX = 4;
const ORIGIN: Offset = { x: 0, y: 0 };

const ACTION_BUTTON_SX = {
  color: "#fff",
  backgroundColor: "rgba(255, 255, 255, 0.12)",
  "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.25)" },
  "&.Mui-disabled": { color: "rgba(255, 255, 255, 0.35)" },
};

const clampScale = (value: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));

export default function ChatImagePreview({
  src,
  alt,
  fileName,
  thumbnailStyle,
  messages,
}: ChatImagePreviewProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [scale, setScale] = useState(MIN_SCALE);
  const [offset, setOffset] = useState<Offset>(ORIGIN);
  const [isDragging, setIsDragging] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ startX: number; startY: number; origin: Offset; moved: boolean } | null>(
    null
  );

  const resetZoom = useCallback(() => {
    setScale(MIN_SCALE);
    setOffset(ORIGIN);
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    resetZoom();
  }, [resetZoom]);

  // Keeps the point under the cursor fixed while the scale changes.
  const zoomTo = useCallback(
    (nextScaleRaw: number, clientX?: number, clientY?: number) => {
      const nextScale = clampScale(nextScaleRaw);
      if (nextScale === MIN_SCALE) {
        resetZoom();
        return;
      }

      const rect = viewportRef.current?.getBoundingClientRect();
      const pointX = rect && clientX !== undefined ? clientX - (rect.left + rect.width / 2) : 0;
      const pointY = rect && clientY !== undefined ? clientY - (rect.top + rect.height / 2) : 0;

      setOffset((current) => ({
        x: pointX - ((pointX - current.x) * nextScale) / scale,
        y: pointY - ((pointY - current.y) * nextScale) / scale,
      }));
      setScale(nextScale);
    },
    [scale, resetZoom]
  );

  const handleWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    zoomTo(scale * (1 - event.deltaY * WHEEL_SCALE_FACTOR), event.clientX, event.clientY);
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      origin: offset,
      moved: false,
    };
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;

    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (!drag.moved && Math.hypot(deltaX, deltaY) > DRAG_CLICK_TOLERANCE_PX) {
      drag.moved = true;
      setIsDragging(scale > MIN_SCALE);
    }

    if (drag.moved && scale > MIN_SCALE) {
      setOffset({ x: drag.origin.x + deltaX, y: drag.origin.y + deltaY });
    }
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    setIsDragging(false);
    if (!drag || drag.moved) return;

    if (event.shiftKey || event.altKey) {
      zoomTo(scale / CLICK_ZOOM_FACTOR, event.clientX, event.clientY);
    } else if (scale >= MAX_SCALE) {
      resetZoom();
    } else {
      zoomTo(scale * CLICK_ZOOM_FACTOR, event.clientX, event.clientY);
    }
  };

  const handleContextMenu = (event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    zoomTo(scale / CLICK_ZOOM_FACTOR, event.clientX, event.clientY);
  };

  const viewportCursor = isDragging ? "grabbing" : scale >= MAX_SCALE ? "zoom-out" : "zoom-in";

  return (
    <>
      <Tooltip title={messages.expandImage}>
        <Box
          component="button"
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label={messages.expandImage}
          sx={{ p: 0, border: 0, background: "none", cursor: "zoom-in", display: "block" }}
        >
          <img src={src} alt={alt} style={thumbnailStyle} />
        </Box>
      </Tooltip>

      <Dialog
        open={isOpen}
        onClose={close}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "95vw",
            height: "92vh",
            maxHeight: "none",
            m: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            boxShadow: "none",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 1,
            p: 1,
            flexShrink: 0,
          }}
        >
          <Tooltip title={messages.zoomOut}>
            <span>
              <IconButton
                onClick={() => zoomTo(scale - BUTTON_SCALE_STEP)}
                disabled={scale <= MIN_SCALE}
                aria-label={messages.zoomOut}
                sx={ACTION_BUTTON_SX}
              >
                <ZoomOutIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Typography
            variant="caption"
            sx={{ color: "#fff", minWidth: 44, textAlign: "center", fontWeight: 600 }}
          >
            {`${Math.round(scale * 100)}%`}
          </Typography>
          <Tooltip title={messages.zoomIn}>
            <span>
              <IconButton
                onClick={() => zoomTo(scale + BUTTON_SCALE_STEP)}
                disabled={scale >= MAX_SCALE}
                aria-label={messages.zoomIn}
                sx={ACTION_BUTTON_SX}
              >
                <ZoomInIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={messages.resetZoom}>
            <span>
              <IconButton
                onClick={resetZoom}
                disabled={scale === MIN_SCALE}
                aria-label={messages.resetZoom}
                sx={ACTION_BUTTON_SX}
              >
                <FitScreenIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={messages.download}>
            <IconButton
              component="a"
              href={src}
              download={fileName}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={messages.download}
              sx={ACTION_BUTTON_SX}
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={messages.closeImagePreview}>
            <IconButton onClick={close} aria-label={messages.closeImagePreview} sx={ACTION_BUTTON_SX}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          ref={viewportRef}
          onWheel={handleWheel}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onContextMenu={handleContextMenu}
          onPointerCancel={() => {
            dragRef.current = null;
            setIsDragging(false);
          }}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: viewportCursor,
            touchAction: "none",
            userSelect: "none",
            px: 2,
            pb: 2,
          }}
        >
          <img
            src={src}
            alt={alt}
            draggable={false}
            style={{
              display: "block",
              width: "100%",
              height: "100%",
              objectFit: "contain",
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              transition: isDragging ? "none" : "transform 0.15s ease-out",
              pointerEvents: "none",
            }}
          />
        </Box>
      </Dialog>
    </>
  );
}
